// Universal product template: generic purchasing-area behavior.
// Two independent pieces, both product-agnostic -- no hardcoded model
// names, pack vocabulary, or product-specific DOM structure. Model/size
// data is read from a Liquid-rendered JSON block (data-uni-sizing-model-info)
// instead of a hardcoded object, so this one file serves any product whose
// catalog matches the documented single-combined-option convention.

(function () {
  'use strict';

  function devWarn(msg) {
    if (window.Shopify && window.Shopify.designMode) {
      console.warn('[product-universal] ' + msg);
    }
  }

  function formatPackLabel(raw) {
    var m = /^(\d+)\s+Pack\s+(.+)$/i.exec(raw || '');
    if (!m) return raw;
    return m[2] + ' (' + m[1] + ' pcs)';
  }

  function markFallback(root) {
    if (root) root.setAttribute('data-uni-selector-fallback', 'true');
  }

  // Fixed root cause (proven against the approved reference implementation
  // in the original theme's own assets/product-gel-box-line.js): kit-
  // variant-picker's own price/gallery/availability/Add-to-Cart logic
  // listens for 'change' via a single delegated listener on itself
  // (this.addEventListener('change', ...) in kit.product-variant-picker.js),
  // which only fires for events that bubble through it as a real DOM
  // ancestor. An earlier version of this class moved the native pack
  // radios out of <kit-variant-picker> into this element's own pack list
  // and never moved them back -- so clicking a pack pill still toggled its
  // own :checked state (native radio grouping is name-based, not DOM-
  // position-based) but the resulting 'change' event no longer bubbled
  // through kit-variant-picker, so nothing it drives ever updated. The fix
  // (rebuildPacks(), below) re-attaches the pack radios' container back
  // into kit-variant-picker itself, exactly like the original does, and --
  // because kit-variant-picker discards and replaces its own children
  // wholesale on every real change -- also re-observes for that and
  // rebuilds from the fresh markup each time, the same requirement the
  // original's own code comments document.
  class UniSizingSelectorElement extends HTMLElement {
    connectedCallback() {
      if (this.dataset.uniReady === 'true') return;
      this.tryInit();
    }

    tryInit() {
      var root = this.closest('[id^="shopify-section-"]');
      var infoScript = this.querySelector('[data-uni-sizing-model-info]');
      var picker = document.querySelector('kit-variant-picker');
      if (!root || !infoScript || !picker) {
        markFallback(root);
        return;
      }

      var modelInfo;
      try {
        modelInfo = JSON.parse(infoScript.textContent);
      } catch (e) {
        devWarn('could not parse sizing model info JSON');
        markFallback(root);
        return;
      }

      var parsed = this.parseRadios(picker, modelInfo);
      if (!parsed.ok) {
        devWarn('sizing selector parse failed: ' + parsed.reason);
        markFallback(root);
        return;
      }

      this.root = root;
      this.picker = picker;
      this.modelInfo = modelInfo;
      this.groups = parsed.groups;
      this.sizesEl = this.querySelector('[data-uni-sizes]');
      this.packsWrap = this.querySelector('[data-uni-packs]');
      this.packListEl = this.querySelector('[data-uni-pack-list]');
      this.sizeButtonsBuilt = false;

      // packsWrap gets moved into kit-variant-picker below (and on every
      // rebuild), which is a separate sibling element outside this
      // component's own subtree -- so any styling scoped to this element's
      // own gbl-picker class would stop matching the pack UI the moment it
      // relocates. Read once, before anything moves, and tag packsWrap
      // itself (the single root of the whole subtree that relocates)
      // directly, exactly like the approved original tags its own moved
      // pill/group elements directly rather than relying on an ancestor
      // that won't travel with them.
      this.isGelBoxFamily = this.classList.contains('gbl-picker');
      if (this.isGelBoxFamily) {
        this.packsWrap.classList.add('gbl-pack-wrap');
      }

      var self = this;
      this.observer = new MutationObserver(function () {
        self.rebuildPacks();
      });

      this.rebuildPacks();
      if (!this.sizeButtonsBuilt) {
        markFallback(root);
        return;
      }

      this.hidden = false;
      this.dataset.uniReady = 'true';
    }

    parseRadios(picker, modelInfo) {
      var radios = picker.querySelectorAll('.kit-radio[data-option-value-id]');
      if (!radios.length) return { ok: false, reason: 'no radio inputs found' };

      var groups = {};
      var knownSizes = {};
      modelInfo.order.forEach(function (name) { knownSizes[name] = true; });

      for (var i = 0; i < radios.length; i++) {
        var label = radios[i];
        var text = (label.textContent || '').trim();
        var parts = text.split(' - ');
        if (parts.length < 2) {
          return { ok: false, reason: 'value "' + text + '" is not formatted "<size> - <pack>"' };
        }
        var size = parts[0].trim();
        var packRaw = parts.slice(1).join(' - ').trim();
        if (!knownSizes[size]) {
          return { ok: false, reason: 'unrecognized size "' + size + '" (not in connected sizing.models list)' };
        }
        var input = label.querySelector('input') || label;
        var available = !label.classList.contains('kit-radio--sold-out') && !label.querySelector('.kit-button--disabled');
        var packText = formatPackLabel(packRaw);
        var textEl = label.querySelector('.kit-button') || label.querySelector('.kit-radio__label');

        if (!groups[size]) groups[size] = [];
        groups[size].push({ label: label, input: input, packText: packText, available: available, textEl: textEl });
      }

      return { ok: true, groups: groups };
    }

    rebuildPacks() {
      // kit-variant-picker's own 'change' handler fetches a fresh Section
      // Rendering API partial on every real variant change (not just in the
      // theme editor) and replaces its own children wholesale with newly
      // server-rendered, unrelocated native radios -- discarding whatever
      // this enhancement had previously moved into packsWrap. This method
      // re-parses those fresh radios and rebuilds the pack groups against
      // them every time, matching the approved original's own required
      // mechanism (not an optional robustness add-on): without it, the
      // custom UI would only survive until the very first real change.
      var result = this.parseRadios(this.picker, this.modelInfo);
      if (!result.ok) {
        devWarn('sizing selector rebuild failed: ' + result.reason);
        return;
      }
      this.groups = result.groups;

      // Disconnected while this method makes its own DOM writes (clearing
      // packListEl, moving packsWrap back into picker) so those self-
      // triggered mutation records never cause a redundant or looping
      // rebuild -- same reasoning as the approved original.
      this.observer.disconnect();

      this.packListEl.innerHTML = '';
      var packGroupEls = {};
      var self = this;
      var order = this.modelInfo.order;

      order.forEach(function (size) {
        if (!self.groups[size]) return;

        if (!self.sizeButtonsBuilt) {
          var info = self.modelInfo.models[size] || {};
          var btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'uni-size-btn';
          btn.setAttribute('aria-pressed', 'false');
          btn.dataset.uniSize = size;
          btn.innerHTML =
            (info.image ? '<img src="' + info.image + '" alt="" loading="lazy" width="40" height="40">' : '') +
            '<span class="uni-size-btn__name">' + size + '</span>' +
            '<span class="uni-size-btn__dims">' +
              '<strong>' + (info.inches || '') + '</strong>' +
              '<small>' + (info.mm || '') + '</small>' +
            '</span>';
          btn.addEventListener('click', function () { self.selectSize(size); });
          self.sizesEl.appendChild(btn);
        }

        var groupEl = document.createElement('div');
        groupEl.className = 'uni-pack-group';
        groupEl.dataset.uniPackGroup = size;
        groupEl.hidden = true;

        self.groups[size].forEach(function (item) {
          item.label.classList.add('uni-pack-pill');
          if (item.textEl) item.textEl.textContent = item.packText;
          if (!item.available) {
            item.label.classList.add('uni-pack-pill--sold-out');
            var badge = document.createElement('span');
            badge.className = 'uni-pack-pill__soldout-badge';
            badge.textContent = 'Sold out';
            item.label.appendChild(badge);
          }
          groupEl.appendChild(item.label);
        });

        var hasAvailable = self.groups[size].some(function (i) { return i.available; });
        if (!hasAvailable) {
          groupEl.classList.add('uni-pack-group--all-sold-out');
          var note = document.createElement('p');
          note.className = 'uni-pack-group__all-sold-out-note';
          note.textContent = 'All pack sizes for ' + size + ' are currently sold out.';
          groupEl.appendChild(note);
        }

        self.packListEl.appendChild(groupEl);
        packGroupEls[size] = groupEl;
      });

      this.packGroupEls = packGroupEls;

      if (Object.keys(packGroupEls).length < 1) {
        devWarn('rebuildPacks: no pack group(s) built');
        this.observer.observe(this.picker, { childList: true });
        return;
      }

      this.sizeButtonsBuilt = true;

      // The relocated radios must stay inside <kit-variant-picker> itself,
      // not just inside this element -- see the diagnosis above and in the
      // class-level comment. Re-attaching packsWrap here (every rebuild,
      // not just the first) keeps the native 'change' bubbling path intact
      // even after kit-variant-picker's own refresh has replaced its other
      // children.
      this.picker.appendChild(this.packsWrap);

      // The native <fieldset> wrapper around the option has nothing left
      // inside it once its radio labels are relocated above -- it comes
      // back fresh (and visible) on every native refresh, so it has to be
      // hidden again on every rebuild, not just the first.
      var nativeFieldset = this.picker.querySelector('.kit-fieldset');
      if (nativeFieldset) nativeFieldset.hidden = true;

      // Restore whichever variant is actually selected server-side (on the
      // first build, the page's real initial selection; on every later
      // rebuild, the pack the shopper -- or this code's own auto-select --
      // just picked, per the fresh HTML kit-variant-picker fetched after
      // the real 'change' event it came from) rather than always forcing
      // the first configured model regardless of the real selection.
      var activeSize = null;
      Object.keys(this.groups).forEach(function (size) {
        if (self.groups[size].some(function (item) { return item.input.checked; })) activeSize = size;
      });
      if (!activeSize) activeSize = Object.keys(this.groups)[0];
      this.selectSize(activeSize, { silent: true });

      this.observer.observe(this.picker, { childList: true });
    }

    selectSize(size, opts) {
      opts = opts || {};
      var self = this;
      Object.keys(this.packGroupEls).forEach(function (s) {
        self.packGroupEls[s].hidden = s !== size;
      });
      this.querySelectorAll('.uni-size-btn').forEach(function (btn) {
        btn.setAttribute('aria-pressed', String(btn.dataset.uniSize === size));
      });
      if (this.packsWrap) this.packsWrap.hidden = false;

      if (!opts.silent && this.groups[size] && this.groups[size][0]) {
        this.groups[size][0].input.click();
      }
    }
  }

  if (!customElements.get('uni-sizing-selector')) {
    customElements.define('uni-sizing-selector', UniSizingSelectorElement);
  }

  // Generic quote-flow visibility sync: mirrors the native Request-a-Quote
  // trigger button's own (already-correct) hidden state onto the
  // secondary in-picker quote link and the secure-checkout row, rather
  // than re-deriving sold-out status independently.
  //
  // For is_gel_box_line_family (detected via the .gbl-picker class
  // universal.sizing-selector.liquid adds only for that family -- see
  // that file), the secondary quote link is intentionally left out of
  // this toggle: it serves a distinct, always-relevant purpose (project
  // quantities / an unavailable pack size) from the large sold-out
  // Request a Quote button this same sync mirrors, and is meant to stay
  // visible alongside it rather than being hidden whenever the big button
  // shows. The matching initial-render half of this exception lives in
  // universal.sizing-selector.liquid (quote_sold_out forced false).
  // Generic (non-gel-box) behavior, and the secure-checkout toggle for
  // every product including gel-box, are both left exactly as they were.
  function initUniQuoteSync() {
    var picker = document.querySelector('kit-variant-picker');
    if (!picker) return;
    var sectionId = picker.dataset.sectionId;
    if (!sectionId) return;

    var nativeTrigger = document.getElementById('RequestQuoteButton-' + sectionId);
    if (!nativeTrigger) return;

    function sync() {
      var hidden = nativeTrigger.hidden;
      if (!document.querySelector('.gbl-picker')) {
        document.querySelectorAll('[data-uni-quote-secondary-wrap]').forEach(function (el) {
          el.hidden = !hidden;
        });
      }
      document.querySelectorAll('[data-uni-secure-checkout]').forEach(function (el) {
        el.hidden = hidden;
      });
    }

    sync();
    new MutationObserver(sync).observe(nativeTrigger, { attributes: true, attributeFilter: ['hidden'] });

    document.querySelectorAll('[data-uni-quote-secondary-trigger]').forEach(function (btn) {
      btn.addEventListener('click', function () { nativeTrigger.click(); });
    });
  }

  // Generic payment-terms placement fix: keeps the native Shop-Pay-style
  // payment-terms message positioned relative to the quote block and its
  // variant-id attribute current across variant switches. One shared
  // implementation for the universal template -- not a per-product copy.
  function initUniPaymentTermsReorder() {
    document.querySelectorAll('kit-variant-picker').forEach(function (picker) {
      var sectionId = picker.dataset.sectionId;
      var productId = picker.dataset.productId;
      var scope = picker.closest('[data-section-id]') || document;
      var terms = scope.querySelector('.shopify-payment-terms');
      var quoteBlock = scope.querySelector('block-request-quote');
      if (terms && quoteBlock && quoteBlock.parentElement) {
        quoteBlock.parentElement.insertBefore(terms, quoteBlock);
      }
      if (terms && sectionId && productId) {
        document.addEventListener('variant:change:' + sectionId + ':' + productId, function (e) {
          var variant = e.detail && e.detail.variant;
          if (variant && variant.id) {
            terms.setAttribute('variant-id', variant.id);
          }
        });
      }
    });
  }

  // Mobile swipe-to-change-model, shared (by duplication, not by reference --
  // see assets/product-prefilled-gel-box-model-details.js's identical copy)
  // between the Happy Connector Fit Guide's generic ARIA-tablist driver
  // below and Gel Box Line's own <gbl-model-tabs> custom element. Host-
  // agnostic: takes the panel elements to listen on, a callback that
  // reports which index is currently active, and a callback to switch to a
  // given index -- neither this function nor its caller renders, styles,
  // or touches anything else, so it carries no risk to either guide's
  // existing content, labels, images, or layout.
  //
  // Deliberately does NOT branch on prefers-reduced-motion: the panel swap
  // itself is an instant hidden-attribute toggle, identical to what already
  // happens on tab click/keyboard today, with no animation added here to
  // suppress. Reduced-motion means no animation, not no interaction.
  function attachModelSwipeNavigation(panels, getActiveIndex, goTo) {
    if (!panels.length) return;
    if (!window.matchMedia || !window.matchMedia('(pointer: coarse)').matches) return;
    // Mirrors devWarn's own designMode check above -- avoids any chance of
    // this gesture fighting the theme editor's own touch handling.
    if (window.Shopify && window.Shopify.designMode) return;

    var THRESHOLD = 48; // px of horizontal travel required to count as a swipe
    var LOCK_RATIO = 1.5; // horizontal must outpace vertical by this much to lock horizontal

    panels.forEach(function (panel) {
      var startX = 0;
      var startY = 0;
      var tracking = false;
      var locked = null; // null (undecided) | 'x' | 'y'

      panel.addEventListener('touchstart', function (e) {
        if (e.touches.length !== 1) { tracking = false; return; }
        // Links, buttons, form controls, and anything explicitly opted out
        // keep their normal tap/drag behavior -- never start tracking a
        // swipe from inside one of them.
        if (e.target.closest && e.target.closest('a, button, input, select, textarea, [data-no-swipe]')) {
          tracking = false;
          return;
        }
        startX = e.touches[0].clientX;
        startY = e.touches[0].clientY;
        tracking = true;
        locked = null;
      }, { passive: true });

      panel.addEventListener('touchmove', function (e) {
        if (!tracking || e.touches.length !== 1) return;
        var dx = e.touches[0].clientX - startX;
        var dy = e.touches[0].clientY - startY;
        if (locked === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) {
          locked = Math.abs(dx) > Math.abs(dy) * LOCK_RATIO ? 'x' : 'y';
        }
        // Only suppress the page's native scroll once a horizontal gesture
        // is confirmed -- an undecided or vertically-locked gesture leaves
        // normal scrolling completely alone.
        if (locked === 'x' && e.cancelable) e.preventDefault();
      }, { passive: false });

      panel.addEventListener('touchend', function (e) {
        if (!tracking) return;
        tracking = false;
        if (locked !== 'x') return;
        var dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) < THRESHOLD) return;
        var activeIndex = getActiveIndex();
        if (activeIndex === -1) return;
        if (dx < 0) {
          // swipe left -> next model, never wrapping past the last one
          if (activeIndex < panels.length - 1) goTo(activeIndex + 1);
        } else {
          // swipe right -> previous model, never wrapping past the first one
          if (activeIndex > 0) goTo(activeIndex - 1);
        }
      }, { passive: true });

      panel.addEventListener('touchcancel', function () {
        tracking = false;
        locked = null;
      }, { passive: true });
    });
  }

  // Generic ARIA-tablist driver for the optional sizing-models module.
  // Drives any [role="tab"]/[role="tabpanel"] structure -- no model-name
  // vocabulary of any kind, so it works for however many tabs a given
  // product's sizing.models list produces.
  function initUniTabs() {
    document.querySelectorAll('[data-uni-tablist]').forEach(function (tablist) {
      var tabs = Array.prototype.slice.call(tablist.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;

      function activate(tab, opts) {
        opts = opts || {};
        tabs.forEach(function (t) {
          var selected = t === tab;
          t.setAttribute('aria-selected', String(selected));
          t.tabIndex = selected ? 0 : -1;
          var panel = document.getElementById(t.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });
        // Swipe-triggered activation (see data-uni-swipe below) explicitly
        // skips focus: moving focus to the tab button would scroll it into
        // view, yanking the page back up to the tab rail the moment someone
        // swipes lower down the panel -- exactly what this feature must not
        // do. Click and keyboard activation keep focusing the tab, same as
        // before.
        if (opts.moveFocus !== false) tab.focus();
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(tab); });
        tab.addEventListener('keydown', function (e) {
          if (e.key === 'ArrowRight') activate(tabs[(i + 1) % tabs.length]);
          if (e.key === 'ArrowLeft') activate(tabs[(i - 1 + tabs.length) % tabs.length]);
        });
      });

      // Opt-in only (data-uni-swipe): this same driver also serves Ray
      // Gel's sections/product-universal-sizing-models.liquid, which must
      // not gain swipe behavior it never asked for. Only the Happy
      // Connector Fit Guide's own tablist markup sets this attribute.
      if (tablist.hasAttribute('data-uni-swipe')) {
        var panels = tabs
          .map(function (t) { return document.getElementById(t.getAttribute('aria-controls')); })
          .filter(Boolean);
        attachModelSwipeNavigation(
          panels,
          function getActiveIndex() {
            return tabs.findIndex(function (t) { return t.getAttribute('aria-selected') === 'true'; });
          },
          function goTo(index) {
            activate(tabs[index], { moveFocus: false });
          }
        );
      }
    });
  }

  document.addEventListener('DOMContentLoaded', function () {
    initUniQuoteSync();
    initUniPaymentTermsReorder();
    initUniTabs();
  });
})();
